/**
 * ComprehensionQuiz Component
 *
 * First exit screen - assesses comprehension of material learned during experiment.
 * Participants must complete all questions to proceed to the demographic survey.
 *
 * Structure:
 * - Quiz questions loaded from game configuration
 * - Questions types: radio (multiple choice), checkbox (select all), text (open-ended)
 * - Per-question measures: confidence + usefulness (+ source when usefulness is low)
 * - Post-quiz confidence assessment
 *
 * Question Difficulty Levels: Easy, Medium, Hard, Challenging
 *
 * Per-Question Measures:
 * 1. Confidence Scale (0-6, compulsory): "How confident are you in your answer?"
 *    - 0 = Not at all confident
 *    - 6 = Very confident
 *
 * 2. Usefulness Scale (0-6, compulsory): "How much did today's session contribute to your answer?"
 *    - 0 = Not at all
 *    - 6 = Entirely
 *
 * 3. Source (conditional - only shown when usefulness <= 3): "...was that because:"
 *    - "knew"    = I already knew this (prior knowledge)
 *    - "guessed" = I just guessed (no relevant knowledge)
 *    Required only when shown; not collected when usefulness > 3.
 *
 * Data Storage:
 * - Quiz answers: player.set("comprehension_quiz", quizAnswers)
 * - Confidence: player.set("comprehension_quiz_confidence", { questionId: 0-6 })
 * - Usefulness: player.set("comprehension_quiz_usefulness", { questionId: 0-6 })
 * - Source: player.set("comprehension_quiz_source", { questionId: "knew"|"guessed" }) (only when usefulness <= 3)
 * - Quiz metadata: player.set("comprehension_quiz_metadata", questionMetadata)
 * - Partial flag: player.set("comprehension_quiz_partial", true/false)
 *   - true = answers auto-saved but not formally submitted
 *   - false = user clicked submit and completed all questions
 * - Post-quiz confidence: player.set("postQuiz_state_confidence", { conf_understand, conf_answer, materialHelpfulness, predictedScore })
 *
 * Auto-Save Behavior:
 * Answers and confidence ratings are auto-saved as they're selected to prevent data loss
 * if the stage timer expires. This ensures we capture partial responses even if the
 * participant doesn't click Submit before time runs out.
 *
 * Validation:
 * All quiz questions must be answered AND have a confidence rating before proceeding
 *
 * Configuration:
 * Questions are loaded from the game configuration which is set based on the treatment.
 * This allows researchers to customize quiz questions without modifying this component.
 */

import { usePlayer, useGame } from "@empirica/core/player/classic/react";
import React, { useState, useEffect } from "react";
import { Button } from "../components/Button";
import { ConfidenceScale, isConfidenceComplete, getEmptyConfidenceState, getPredictionLabel } from "../components/ConfidenceScale";
import { LatexMessage } from "../components/chat/LatexMessage";

// Quiz questions are now loaded from config, not hardcoded
// Keeping this structure for reference of the old format
const quizQuestions_DEPRECATED = [
  // Question 1: Multiple Choice (Radio)
  {
    id: 1,
    type: "radio",
    question: "Recall the event that directly triggered the Watermelon Riot in Panama City in 1856.",
    name: "q1_trigger",
    options: [
      "A. A group of American travelers refused to pay railroad fares.",
      "B. An American passenger took a slice of watermelon from a local vendor and refused to pay.",
      "C. A dispute arose between Panamanian police and U.S. sailors at the docks.",
      "D. A Panamanian worker attacked a railroad guard."
    ]
  },
  // Question 2: Select All That Apply (Checkbox)
  {
    id: 2,
    type: "checkbox",
    question: "Identify the factors that contributed to growing tensions leading up to the Watermelon Riot. Select all that apply.",
    name: "q2_tensions",
    options: [
      "A. Economic resentment caused by local job losses after the completion of the Panama Railroad",
      "B. The presence of armed American filibusters in Panama City",
      "C. Widespread anti-British sentiment over control of trade routes",
      "D. Cultural conflicts between local residents and transient American travelers",
      "E. U.S. enforcement of the Mallarino-Bidlack Treaty granting it rights to intervene in Panama"
    ]
  },
  // Question 3: Multiple Choice (Radio)
  {
    id: 3,
    type: "radio",
    question: "Explain why the Watermelon Riot is considered a reflection of broader tensions between local Panamanians and Americans traveling through the isthmus.",
    name: "q3_reflection",
    options: [
      "A. It demonstrated how American travelers respected local customs during their journey.",
      "B. It showed that trade between Panama and the United States was balanced and peaceful.",
      "C. It revealed growing resentment toward American dominance, economic disruption, and cultural arrogance.",
      "D. It proved that the Panamanians initiated violence without provocation."
    ]
  },
  // Question 4: Select All That Apply (Checkbox)
  {
    id: 4,
    type: "checkbox",
    question: "Categorize the following outcomes as either immediate effects or long-term consequences of the Watermelon Riot. Select all that represent immediate effects.",
    name: "q4_immediate_effects",
    options: [
      "A. The deaths of several Americans and Panamanians during the riot",
      "B. The destruction of property, including parts of the railroad station",
      "C. The signing of the Cass-Herran Treaty (1858) to resolve claims",
      "D. Temporary U.S. military intervention to restore order",
      "E. The eventual independence of Panama from Colombia in 1903"
    ]
  },
  // Question 5: Select All That Apply (Checkbox)
  {
    id: 5,
    type: "checkbox",
    question: "Apply your understanding of U.S.–Panamanian relations during the mid-19th century to identify which situations would likely have increased the chance of another riot like the Watermelon Riot. Select all that apply.",
    name: "q5_increased_riot",
    options: [
      "A. U.S. travelers showing greater respect for local laws and customs",
      "B. American merchants monopolizing local trade routes through Panama",
      "C. Panamanian workers being excluded from railroad jobs and profits",
      "D. A joint cultural exchange between Panamanians and Americans",
      "E. Reduced U.S. military presence in Panama City"
    ]
  },
  // Question 6: Open Ended (Text Input)
  {
    id: 6,
    type: "text",
    question: "Predict how the outcome of the Watermelon Riot might have changed if U.S. authorities had immediately compensated Panamanian victims instead of demanding reparations. Maximum 300 characters",
    name: "q6_prediction",
    maxLength: 300,
  }
];

export function ComprehensionQuiz({ next }) {
  const player = usePlayer();
  const game = useGame();

  // Load quiz configuration from game
  const config = game.get("config");

  // Game mode (needed early for staged-quiz sourcing below)
  const gameMode = game.get("gameMode") || "dyad";
  const isSingleMode = gameMode === "single";

  // Full shuffled set (all 12) — used for scoring and metadata.
  const allQuizQuestions = player.get("quizQuestions") || config?.quiz?.questions || [];

  // Post-learning quiz: the PRE half was already answered (closed-book baseline)
  // in the Pre-Learning Quiz round, so this screen renders ONLY the POST half.
  // Applies to both DYAD and SINGLE. Falls back to the full set if no split.
  const quizSplit = game.get("quizSplit");
  const preIds = quizSplit?.preIds || null;
  const postIds = quizSplit?.postIds || null;
  const quizQuestions = postIds
    ? postIds.map((id) => allQuizQuestions.find((q) => q.id === id)).filter(Boolean)
    : allQuizQuestions;
  // Declared here (before any early return) — the post-quiz confidence screen
  // references totalQuestions, and it returns above the old declaration site,
  // so declaring it late caused a TDZ ReferenceError → blank screen.
  const totalQuestions = quizQuestions.length;

  // Pre-learning answers (baseline), merged in for scoring so comprehension_score
  // covers all 12 questions.
  const preAnswers = player.get("quiz_pre_answers") || {};

  const quizTitle = config?.quiz?.title || "Knowledge Assessment";
  const quizInstructions = config?.quiz?.instructions || "Answer the following questions based on what you learned during the study.";
  const topicName = config?.name || "the topic";

  // Pre-quiz prediction (shown BEFORE the questions): "of the next N, how many
  // will you get right?" (0-N). Resumes from any saved value.
  const [prePrediction, setPrePrediction] = useState(() => {
    const p = player.get("post_quiz_prediction");
    return p === undefined ? null : p;
  });
  // Teacher-only partner-score prediction, moved here from Post-Chat Reflection so
  // it sits on this pre-quiz screen next to the self prediction. Same 0..N scale
  // (N = totalQuestions = the post-half the partner also answers). Saved as
  // "partner_quiz_prediction".
  const [partnerPrediction, setPartnerPrediction] = useState(() => {
    const p = player.get("partner_quiz_prediction");
    return p === undefined ? null : p;
  });
  // Show the pre-quiz screen until every required prediction is recorded: the self
  // prediction (all roles) plus, for the dyad teacher, the partner prediction.
  const [showPrePrediction, setShowPrePrediction] = useState(() => {
    const selfDone = player.get("post_quiz_prediction") !== undefined;
    const isDyadTeacher = !isSingleMode && player.get("marker") === 0;
    const partnerDone = player.get("partner_quiz_prediction") !== undefined;
    return !(selfDone && (!isDyadTeacher || partnerDone));
  });

  // Quiz states
  const [quizAnswers, setQuizAnswers] = useState({});
  const [questionConfidence, setQuestionConfidence] = useState({}); // 0-6 confidence in the answer (compulsory)
  const [questionUsefulness, setQuestionUsefulness] = useState({}); // 0-6 how much today contributed (compulsory)
  const [questionSource, setQuestionSource] = useState({}); // "knew" | "guessed" — only when usefulness <= 3
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [showScore, setShowScore] = useState(false);
  const [score, setScore] = useState({ correct: 0, total: 0, percentage: 0 });

  // Post-quiz confidence state (2-item scale)
  const [showPostQuizConfidence, setShowPostQuizConfidence] = useState(false);
  const [postQuizConfidence, setPostQuizConfidence] = useState(getEmptyConfidenceState());
  const [materialHelpfulness, setMaterialHelpfulness] = useState(null);
  const [predictedScore, setPredictedScore] = useState(null);

  // Post-quiz communication assessment (role-specific)
  const [postQuizCommunication, setPostQuizCommunication] = useState({
    explanationClarity: null,
    explanationHelpfulness: null,
  });

  // Persist question metadata immediately (not only on formal submit) so that
  // timer-expired sessions can still be scored offline from the partial answers
  useEffect(() => {
    if (allQuizQuestions.length === 0) return;
    if (player.get("comprehension_quiz_metadata")) return;
    // Metadata covers ALL questions (early + final) so offline scoring works.
    const questionMetadata = allQuizQuestions.reduce((acc, q) => {
      acc[q.id] = {
        difficulty: q.difficulty || '',
        type: q.type || 'checkbox',
        correctAnswer: q.correctAnswer || ''
      };
      return acc;
    }, {});
    player.set("comprehension_quiz_metadata", questionMetadata);
  }, []);

  // Get player marker for color styling (gameMode/isSingleMode computed above)
  const marker = player.get("marker");
  const assignedRole = player.get("assignedRole");
  const condition = game.get("condition") || "pdf"; // ai, pdf, ai-fixed, ai-pdf, web

  // Determine role for styling
  const isLearner = isSingleMode || assignedRole === "Learner";
  const isTeacher = !isSingleMode && marker === 0;
  const isStudent = !isSingleMode && marker > 0;

  // Get role string for prediction label
  const roleForLabel = isLearner ? "learner" : (isTeacher ? "teacher" : "student");

  // Role-based styling
  const roleColor = isLearner ? "violet" : (isTeacher ? "blue" : "emerald");
  const roleName = isLearner ? "Learner" : (isTeacher ? "Teacher" : "Student");
  const headerBgClass = isLearner
    ? "bg-violet-600"
    : isTeacher
    ? "bg-blue-600"
    : "bg-emerald-600";
  const buttonClass = isLearner
    ? "bg-violet-600 hover:bg-violet-700"
    : isTeacher
    ? "bg-blue-600 hover:bg-blue-700"
    : "bg-emerald-600 hover:bg-emerald-700";
  const accentBorderClass = isLearner
    ? "border-violet-500"
    : isTeacher
    ? "border-blue-500"
    : "border-emerald-500";
  const accentBgClass = isLearner
    ? "bg-violet-50"
    : isTeacher
    ? "bg-blue-50"
    : "bg-emerald-50";
  const accentTextClass = isLearner
    ? "text-violet-800"
    : isTeacher
    ? "text-blue-800"
    : "text-emerald-800";

  // The source clarifier ("already knew" vs "just guessed") is shown only when
  // today's usefulness is low (0-3). When shown it is compulsory; otherwise it
  // is not collected and not required to complete the question.
  const USEFULNESS_SOURCE_THRESHOLD = 3;

  // Single source of truth for "is this question fully answered?" — used by the
  // submit gate, the progress dots, the per-question status, and the counter.
  const isQuestionAnswered = (q) => {
    if (!q) return false;
    const answer = quizAnswers[q.id];
    const hasAnswer = q.type === 'text'
      ? !!(answer && answer.trim().length > 0)
      : q.type === 'checkbox'
      ? !!(answer && answer.length > 0)
      : !!answer;
    const usefulness = questionUsefulness[q.id];
    const hasConfidence = questionConfidence[q.id] !== null && questionConfidence[q.id] !== undefined;
    const hasUsefulness = usefulness !== null && usefulness !== undefined;
    const hasSource = questionSource[q.id] !== null && questionSource[q.id] !== undefined;
    // Source required only when the clarifier is shown (usefulness <= threshold)
    const sourceOk = hasUsefulness && (usefulness > USEFULNESS_SOURCE_THRESHOLD || hasSource);
    return hasAnswer && hasConfidence && hasUsefulness && sourceOk;
  };

  function handleSubmit(event) {
    event.preventDefault();

    // Require at least one question to be answered (prevent empty quiz submission)
    if (quizQuestions.length === 0) {
      console.error("No quiz questions found in config");
      setQuizSubmitted(true);
      return;
    }

    // Check if all quiz questions are answered AND have confidence + usefulness
    // (+ source where the clarifier is shown)
    const allQuizAnswered = quizQuestions.every(isQuestionAnswered);

    if (!allQuizAnswered) {
      setQuizSubmitted(true);
      return;
    }

    // Save post-learning answers + per-question battery. These cover the POST
    // half only; the PRE half lives in quiz_pre_answers.
    player.set("comprehension_quiz", quizAnswers);
    player.set("comprehension_quiz_confidence", questionConfidence); // 0-6 scale
    player.set("comprehension_quiz_usefulness", questionUsefulness); // 0-6 scale (today's contribution)
    player.set("comprehension_quiz_source", questionSource); // "knew" | "guessed" (only when usefulness <= 3)
    player.set("comprehension_quiz_partial", false); // Mark as fully submitted

    // Save question metadata for analysis (difficulty, question type, correct answers).
    // Covers ALL questions (early + final).
    const questionMetadata = allQuizQuestions.reduce((acc, q) => {
      acc[q.id] = {
        difficulty: q.difficulty || '',
        type: q.type || 'checkbox',
        correctAnswer: q.correctAnswer || ''
      };
      return acc;
    }, {});
    player.set("comprehension_quiz_metadata", questionMetadata);

    // Score a subset of questions against a given answers object. Text questions
    // (no correctAnswer) are excluded from scoring.
    const scoreSubset = (questionList, answersObj) => {
      let correct = 0;
      let total = 0;
      questionList.forEach(q => {
        if (q.type === 'text' || !q.correctAnswer) return;
        total++;
        const userAnswer = answersObj[q.id];
        const correctAnswer = q.correctAnswer;
        if (q.type === 'checkbox') {
          const userSet = new Set(Array.isArray(userAnswer) ? userAnswer : []);
          const correctSet = new Set(Array.isArray(correctAnswer) ? correctAnswer : [correctAnswer]);
          if (userSet.size === correctSet.size && [...userSet].every(v => correctSet.has(v))) correct++;
        } else if (userAnswer === correctAnswer) {
          correct++;
        }
      });
      const percentage = total > 0 ? Math.round((correct / total) * 100) : 0;
      return { correct, total, percentage };
    };

    // Score the PRE half (baseline, answered before learning) and the POST half
    // (the learning outcome) SEPARATELY — they measure different things and must
    // not be conflated. `comprehension_score` = the post (outcome) half.
    const preQuestions = preIds ? allQuizQuestions.filter(q => preIds.includes(q.id)) : [];
    const postQuestions = quizQuestions; // already the rendered post subset (or full set if no split)

    const postScore = scoreSubset(postQuestions, quizAnswers);
    player.set("comprehension_score", postScore); // post (outcome) half

    if (preQuestions.length > 0) {
      player.set("quiz_pre_score", scoreSubset(preQuestions, preAnswers)); // baseline half
    }

    // Show post-quiz confidence screen instead of proceeding directly
    setShowPostQuizConfidence(true);
  }

  // Auto-save post-quiz fields as they are answered. The quiz stage has a
  // HIDDEN 20-minute timer; if it expires while the participant is on this
  // screen, only persisted values survive. partial:true is cleared on submit.
  useEffect(() => {
    if (!showPostQuizConfidence) return;

    const anyConfidenceAnswered =
      Object.values(postQuizConfidence).some((v) => v !== null) ||
      materialHelpfulness !== null ||
      predictedScore !== null;
    if (anyConfidenceAnswered) {
      player.set("postQuiz_state_confidence", {
        ...postQuizConfidence,
        materialHelpfulness,
        predictedScore,
        partial: true,
        topicId: config?.id,
        timestamp: new Date().toISOString(),
      });
    }

  }, [showPostQuizConfidence, postQuizConfidence, materialHelpfulness, predictedScore]);

  function handlePostQuizConfidenceChange(key, value) {
    setPostQuizConfidence((prev) => ({ ...prev, [key]: value }));
  }

  function handlePostQuizCommunicationChange(key, value) {
    setPostQuizCommunication((prev) => ({ ...prev, [key]: value }));
  }

  function handlePostQuizConfidenceSubmit() {
    // Save post-quiz confidence (2-item scale + material helpfulness + prediction)
    player.set("postQuiz_state_confidence", {
      ...postQuizConfidence,
      materialHelpfulness,
      predictedScore,
      partial: false,
      topicId: config?.id,
      timestamp: new Date().toISOString(),
    });

    // (Post-quiz communication assessment removed — it duplicated the post-chat
    // clarity/helpfulness items.)
    next();
  }

  function handleContinue() {
    next();
  }

  const handleQuizChange = (e) => {
    const { name, value, type, checked } = e.target;

    setQuizAnswers(prevAnswers => {
      let newAnswers = { ...prevAnswers };

      if (type === 'radio' || type === 'textarea') {
        newAnswers[name] = value;
      } else if (type === 'checkbox') {
        let currentSelections = newAnswers[name] || [];
        if (checked) {
          newAnswers[name] = [...currentSelections, value];
        } else {
          newAnswers[name] = currentSelections.filter(v => v !== value);
        }
        if (newAnswers[name].length === 0) {
          delete newAnswers[name];
        }
      }

      // Auto-save answers as they're selected (prevents data loss if timer expires)
      player.set("comprehension_quiz", newAnswers);
      player.set("comprehension_quiz_partial", true); // Flag indicating not fully submitted

      return newAnswers;
    });

    setQuizSubmitted(false);
  };

  const handleQuestionConfidenceChange = (questionId, value) => {
    setQuestionConfidence(prev => {
      const newConfidence = { ...prev, [questionId]: value };
      // Auto-save confidence as selected
      player.set("comprehension_quiz_confidence", newConfidence);
      return newConfidence;
    });
    setQuizSubmitted(false);
  };

  const handleQuestionUsefulnessChange = (questionId, value) => {
    setQuestionUsefulness(prev => {
      const newUsefulness = { ...prev, [questionId]: value };
      // Auto-save usefulness as selected
      player.set("comprehension_quiz_usefulness", newUsefulness);
      return newUsefulness;
    });
    // If usefulness rises above the threshold the source clarifier is hidden,
    // so clear any previously-recorded source for this question.
    if (value > USEFULNESS_SOURCE_THRESHOLD) {
      setQuestionSource(prev => {
        if (prev[questionId] === undefined) return prev;
        const newSource = { ...prev };
        delete newSource[questionId];
        player.set("comprehension_quiz_source", newSource);
        return newSource;
      });
    }
    setQuizSubmitted(false);
  };

  const handleQuestionSourceChange = (questionId, value) => {
    setQuestionSource(prev => {
      const newSource = { ...prev, [questionId]: value };
      // Auto-save source as selected
      player.set("comprehension_quiz_source", newSource);
      return newSource;
    });
    setQuizSubmitted(false);
  };

  // Check if text contains HTML (for image rendering)
  const containsHtml = (text) => /<[^>]+>/.test(text);

  // Scale (0-6)
  const SCALE_0_6 = [0, 1, 2, 3, 4, 5, 6];

  // Renders confidence, usefulness, and (conditionally) source for each question
  const renderQuestionMeasures = (questionId) => {
    const currentConfidence = questionConfidence[questionId];
    const currentUsefulness = questionUsefulness[questionId];
    const currentSource = questionSource[questionId];
    // Source clarifier shows only once usefulness is answered and is low (<= threshold)
    const showSource =
      currentUsefulness !== null &&
      currentUsefulness !== undefined &&
      currentUsefulness <= USEFULNESS_SOURCE_THRESHOLD;
    const colorClasses = {
      violet: "bg-violet-600 text-white shadow-md",
      blue: "bg-blue-600 text-white shadow-md",
      emerald: "bg-emerald-600 text-white shadow-md"
    };
    const colorClass = colorClasses[roleColor];

    return (
      <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
        {/* 1. Confidence Scale */}
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-2">
            How confident are you in your answer?
          </label>
          <div className="flex items-center gap-1 justify-center">
            <span className="text-xs text-gray-500 w-16 text-right">Not at all</span>
            <div className="flex gap-1">
              {SCALE_0_6.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => handleQuestionConfidenceChange(questionId, v)}
                  className={`w-9 h-9 rounded-lg font-medium text-sm transition-all ${
                    currentConfidence === v
                      ? colorClass
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
            <span className="text-xs text-gray-500 w-16">Very</span>
          </div>
        </div>

        {/* 2. Usefulness Scale (today's contribution to the answer) */}
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-2">
            How much did today's session contribute to your answer?
          </label>
          <div className="flex items-center gap-1 justify-center">
            <span className="text-xs text-gray-500 w-16 text-right">Not at all</span>
            <div className="flex gap-1">
              {SCALE_0_6.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => handleQuestionUsefulnessChange(questionId, v)}
                  className={`w-9 h-9 rounded-lg font-medium text-sm transition-all ${
                    currentUsefulness === v
                      ? colorClass
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
            <span className="text-xs text-gray-500 w-16">Entirely</span>
          </div>
        </div>

        {/* 3. Source (only when today's usefulness is low) */}
        {showSource && (
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">
              You answered this mostly on your own — was that because:
            </label>
            <div className="flex gap-2 justify-center">
              <button
                type="button"
                onClick={() => handleQuestionSourceChange(questionId, "knew")}
                className={`flex-1 max-w-[160px] py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                  currentSource === "knew"
                    ? colorClass
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                I already knew this
              </button>
              <button
                type="button"
                onClick={() => handleQuestionSourceChange(questionId, "guessed")}
                className={`flex-1 max-w-[160px] py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                  currentSource === "guessed"
                    ? colorClass
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                I just guessed
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderCheckboxQuestion = (q, index) => {
    const currentAnswer = quizAnswers[q.id] || [];
    const questionHasHtml = containsHtml(q.text);

    return (
      <div key={q.id} className="bg-white rounded-xl shadow-lg border border-gray-200">
        {/* Question Content */}
        <div className="p-6">
          {questionHasHtml ? (
            <h3 className="font-semibold text-gray-900 text-lg mb-4 leading-relaxed" dangerouslySetInnerHTML={{ __html: q.text }} />
          ) : (
            <h3 className="font-semibold text-gray-900 text-lg mb-4 leading-relaxed">
              <LatexMessage text={q.text} />
            </h3>
          )}
          <p className="text-sm text-gray-500 mb-4">Select all that apply</p>
          <div className="space-y-3">
            {q.options.map((option, optIndex) => {
              const labelHasHtml = containsHtml(option.label);
              const isSelected = currentAnswer.includes(option.value);
              return (
                <label key={optIndex} className={`flex items-start cursor-pointer p-4 rounded-lg border-2 transition-all ${isSelected ? `${accentBgClass} ${accentBorderClass}` : 'bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-gray-300'}`}>
                  <input
                    type="checkbox"
                    name={q.id}
                    value={option.value}
                    checked={isSelected}
                    onChange={handleQuizChange}
                    className={`mr-3 mt-0.5 h-5 w-5 rounded focus:ring-2 ${isLearner ? 'text-violet-600 focus:ring-violet-500' : isTeacher ? 'text-blue-600 focus:ring-blue-500' : 'text-emerald-600 focus:ring-emerald-500'}`}
                  />
                  {labelHasHtml ? (
                    <span className="text-sm text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: option.label }} />
                  ) : (
                    <span className="text-sm text-gray-700 leading-relaxed">
                      <LatexMessage text={option.label} />
                    </span>
                  )}
                </label>
              );
            })}
          </div>
          {renderQuestionMeasures(q.id)}
        </div>
      </div>
    );
  };

  const renderTextQuestion = (q, index) => {
    const currentAnswer = quizAnswers[q.id] || '';
    const questionHasHtml = containsHtml(q.text);

    const focusRingClass = isLearner
      ? 'focus:ring-violet-500 focus:border-violet-500'
      : isTeacher
      ? 'focus:ring-blue-500 focus:border-blue-500'
      : 'focus:ring-emerald-500 focus:border-emerald-500';

    return (
      <div key={q.id} className="bg-white rounded-xl shadow-lg border border-gray-200">
        {/* Question Content */}
        <div className="p-6">
          {questionHasHtml ? (
            <h3 className="font-semibold text-gray-900 text-lg mb-4 leading-relaxed" dangerouslySetInnerHTML={{ __html: q.text }} />
          ) : (
            <h3 className="font-semibold text-gray-900 text-lg mb-4 leading-relaxed">
              <LatexMessage text={q.text} />
            </h3>
          )}
          <textarea
            name={q.id}
            value={currentAnswer}
            onChange={handleQuizChange}
            maxLength={q.charLimit}
            rows={5}
            className={`w-full p-4 border-2 border-gray-200 rounded-lg ${focusRingClass} transition-all text-base`}
            placeholder="Type your response here..."
          />
          <p className="text-sm text-gray-500 mt-2 text-right">
            {currentAnswer.length} / {q.charLimit} characters
          </p>
          {renderQuestionMeasures(q.id)}
        </div>
      </div>
    );
  };

  const renderQuizQuestion = (q, index) => {
    if (q.type === "checkbox") return renderCheckboxQuestion(q, index);
    if (q.type === "text") return renderTextQuestion(q, index);

    // Default/Radio Renderer (single-select)
    const currentAnswer = quizAnswers[q.id];
    const questionHasHtml = containsHtml(q.text);

    return (
      <div key={q.id} className="bg-white rounded-xl shadow-lg border border-gray-200">
        {/* Question Content */}
        <div className="p-6">
          {questionHasHtml ? (
            <h3 className="font-semibold text-gray-900 text-lg mb-4 leading-relaxed" dangerouslySetInnerHTML={{ __html: q.text }} />
          ) : (
            <h3 className="font-semibold text-gray-900 text-lg mb-4 leading-relaxed">
              <LatexMessage text={q.text} />
            </h3>
          )}
          <p className="text-sm text-gray-500 mb-4">Select one answer</p>
          <div className="space-y-3">
            {q.options.map((option, optIndex) => {
              const labelHasHtml = containsHtml(option.label);
              const isSelected = currentAnswer === option.value;
              return (
                <label key={optIndex} className={`flex items-start cursor-pointer p-4 rounded-lg border-2 transition-all ${isSelected ? `${accentBgClass} ${accentBorderClass}` : 'bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-gray-300'}`}>
                  <input
                    type="radio"
                    name={q.id}
                    value={option.value}
                    checked={isSelected}
                    onChange={handleQuizChange}
                    className={`mr-3 mt-0.5 h-5 w-5 focus:ring-2 ${isLearner ? 'text-violet-600 focus:ring-violet-500' : isTeacher ? 'text-blue-600 focus:ring-blue-500' : 'text-emerald-600 focus:ring-emerald-500'}`}
                  />
                  {labelHasHtml ? (
                    <span className="text-sm text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: option.label }} />
                  ) : (
                    <span className="text-sm text-gray-700 leading-relaxed">
                      <LatexMessage text={option.label} />
                    </span>
                  )}
                </label>
              );
            })}
          </div>
          {renderQuestionMeasures(q.id)}
        </div>
      </div>
    );
  };

  // Show post-quiz confidence screen after quiz submission
  if (showPostQuizConfidence) {
    // Full 2-item confidence (no exclusions) + material helpfulness + prediction + communication (dyad only)
    const confidenceComplete = isConfidenceComplete(postQuizConfidence, []) && materialHelpfulness !== null && predictedScore !== null;
    // Post-quiz communication assessment (clarity/helpfulness) removed as a
    // duplicate of the post-chat items; no longer gates completion.
    const allComplete = confidenceComplete;

    const selectedColorClass = {
      violet: "bg-violet-600 text-white shadow-md scale-110",
      blue: "bg-blue-600 text-white shadow-md scale-110",
      emerald: "bg-emerald-600 text-white shadow-md scale-110"
    }[roleColor];

    // Communication assessment questions (role-specific)
    const communicationQuestions = isTeacher
      ? [
          {
            key: "explanationClarity",
            label: "How clear were your explanations in relation to what the quiz asked?",
            lowLabel: "Very unclear",
            highLabel: "Very clear",
          },
          {
            key: "explanationHelpfulness",
            label: "How helpful was your teaching in helping your partner answer the quiz?",
            lowLabel: "Not helpful",
            highLabel: "Very helpful",
          },
        ]
      : [
          {
            key: "explanationClarity",
            label: "How clear were the explanations in relation to what the quiz asked?",
            lowLabel: "Very unclear",
            highLabel: "Very clear",
          },
          {
            key: "explanationHelpfulness",
            label: "How helpful was the teaching you received in helping you answer the quiz?",
            lowLabel: "Not helpful",
            highLabel: "Very helpful",
          },
        ];

    return (
      <div className="h-full overflow-y-auto bg-gray-50 py-6 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className={`${headerBgClass} rounded-xl p-5 mb-6 text-white text-center`}>
            <span className="text-sm font-medium uppercase tracking-wide opacity-80">{roleName}</span>
            <h2 className="text-xl font-bold mt-1">Post-Quiz Assessment</h2>
            <p className="text-sm opacity-80 mt-1">
              You completed the quiz about <strong>{topicName}</strong>
            </p>
          </div>

          {/* Post-Quiz Assessment Card */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            {/* 1. Predicted Score Rating FIRST (0..N scale — postdiction of the post half, N = totalQuestions) */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                1. {getPredictionLabel(condition, roleForLabel, "postQuiz", totalQuestions)}
              </label>
              <div className={`flex ${totalQuestions > 6 ? "gap-0.5" : "gap-1"} flex-1 justify-center`}>
                {Array.from({ length: totalQuestions + 1 }, (_, v) => v).map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setPredictedScore(v)}
                    className={`${totalQuestions > 6 ? "w-8 h-8 text-xs" : "w-10 h-10 text-sm"} rounded-lg font-medium transition-all ${
                      predictedScore === v
                        ? selectedColorClass
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>

            {/* 2-3. State Confidence (2 items with "After the quiz" prefix) */}
            <div className="mt-4">
              <ConfidenceScale
                title="After the Quiz"
                instructions="Now that you've completed the quiz, rate your final confidence about this topic:"
                values={postQuizConfidence}
                onChange={handlePostQuizConfidenceChange}
                color={roleColor}
                topicName={topicName}
                excludeQuestions={[]}
                startNumber={2}
                questionPrefix="After the quiz, "
                labelOverrides={{
                  // They have just answered the quiz, so "could answer questions"
                  // reads oddly here — reword to be about further questions.
                  conf_answer: "How certain are you that you could answer further questions about this topic?",
                }}
              />
            </div>

            {/* 4. Material Helpfulness Rating */}
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                4. {isTeacher || isSingleMode
                  ? "How helpful was the learning material for understanding the topic?"
                  : "How helpful was your partner's explanation for understanding the topic?"}
              </label>
              <div className="flex items-center gap-2">
                <span className="text-xs w-24 text-gray-500 text-right">
                  Not at all helpful
                </span>
                <div className="flex gap-1 flex-1 justify-center">
                  {[0, 1, 2, 3, 4, 5, 6].map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setMaterialHelpfulness(v)}
                      className={`w-10 h-10 rounded-lg font-medium transition-all ${
                        materialHelpfulness === v
                          ? selectedColorClass
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
                <span className="text-xs w-24 text-gray-500">
                  Very helpful
                </span>
              </div>
            </div>

            {/* Communication assessment (clarity/helpfulness) removed here —
                it duplicated the post-chat screen and the single explanation
                question now lives only there. */}

            {/* Submit Button */}
            <div className="mt-6">
              <button
                onClick={handlePostQuizConfidenceSubmit}
                disabled={!allComplete}
                className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-all duration-200 ${
                  allComplete ? buttonClass : 'bg-gray-400 cursor-not-allowed'
                }`}
              >
                {allComplete ? "Continue to Survey" : "Please answer all questions"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show score screen after submission
  if (showScore) {
    return (
      <div className="py-8 max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mt-12 text-center">
          {/* Score Card */}
          <div className="bg-gray-50 border-gray-200 border-2 rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Your Score</h2>
            <p className="text-gray-600 mb-6">Knowledge assessment on <strong>{topicName}</strong></p>

            {/* Score Circle */}
            <div className="relative w-40 h-40 mx-auto mb-6">
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                <circle
                  cx="50" cy="50" r="45"
                  stroke="#e5e7eb" strokeWidth="8" fill="none"
                />
                <circle
                  cx="50" cy="50" r="45"
                  stroke="#3b82f6"
                  strokeWidth="8" fill="none"
                  strokeLinecap="round"
                  strokeDasharray={`${score.percentage * 2.83} 283`}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-4xl font-bold text-gray-900">{score.percentage}%</span>
                <span className="text-sm text-gray-500">{score.correct}/{score.total} correct</span>
              </div>
            </div>
          </div>

          {/* Info */}
          <p className="text-gray-600 mb-8">
            Your responses have been recorded. Click continue to proceed to the final survey.
          </p>

          {/* Continue Button */}
          <Button onClick={handleContinue}>
            Continue to Survey
          </Button>
        </div>
      </div>
    );
  }

  // Calculate progress (totalQuestions declared near the top — see note there)
  const completedCount = quizQuestions.filter(isQuestionAnswered).length;

  // Pre-quiz prediction screen — shown BEFORE the questions. "Of the next N
  // questions, how many will you get right?" (0-N). Saved as post_quiz_prediction.
  if (showPrePrediction && totalQuestions > 0) {
    const handlePrePredict = (v) => {
      setPrePrediction(v);
      player.set("post_quiz_prediction", v); // auto-save
    };
    const handlePartnerPredict = (v) => {
      setPartnerPrediction(v);
      player.set("partner_quiz_prediction", v); // auto-save
    };
    // Ready to start once the self prediction is set (all roles) and — for the
    // dyad teacher — the partner prediction too.
    const preReady =
      prePrediction !== null && prePrediction !== undefined &&
      (!isTeacher || (partnerPrediction !== null && partnerPrediction !== undefined));
    return (
      <div className="h-full overflow-y-auto bg-gray-50 py-6 px-4">
        <div className="max-w-2xl mx-auto">
          <div className={`${headerBgClass} rounded-xl p-5 mb-6 text-white`}>
            <span className="text-sm font-medium uppercase tracking-wide opacity-80">{roleName}</span>
            <h2 className="text-xl font-bold mt-1">Before the quiz</h2>
            <p className="text-sm opacity-80 mt-1">{topicName}</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 space-y-6">
            {/* Self prediction (all roles) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                You're about to answer {totalQuestions} questions. How many do you think you'll get right?
              </label>
              <div className="flex gap-1 justify-center">
                {Array.from({ length: totalQuestions + 1 }, (_, v) => v).map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => handlePrePredict(v)}
                    className={`w-10 h-10 rounded-lg font-medium transition-all ${
                      prePrediction === v ? `${buttonClass} text-white shadow-md scale-110` : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>

            {/* Partner prediction (dyad teacher only) — moved from Post-Chat Reflection */}
            {isTeacher && (
              <div className="pt-4 border-t border-gray-100">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {getPredictionLabel(condition, "teacher", "partner", totalQuestions)}
                </label>
                <div className="flex gap-1 justify-center">
                  {Array.from({ length: totalQuestions + 1 }, (_, v) => v).map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => handlePartnerPredict(v)}
                      className={`w-10 h-10 rounded-lg font-medium transition-all ${
                        partnerPrediction === v ? `${buttonClass} text-white shadow-md scale-110` : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <button
                type="button"
                onClick={() => setShowPrePrediction(false)}
                disabled={!preReady}
                className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-all ${
                  preReady ? buttonClass : "bg-gray-400 cursor-not-allowed"
                }`}
              >
                Start the quiz
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Current question helpers
  const currentQuestion = quizQuestions[currentQuestionIndex];
  const isCurrentComplete = isQuestionAnswered(currentQuestion);

  const canGoNext = currentQuestionIndex < totalQuestions - 1;
  const canGoPrev = currentQuestionIndex > 0;
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;
  const allComplete = completedCount === totalQuestions;

  const handleNext = () => {
    if (canGoNext) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (canGoPrev) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleJumpToQuestion = (index) => {
    setCurrentQuestionIndex(index);
  };

  return (
    <div className="h-full overflow-y-auto bg-gray-50 py-6 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className={`${headerBgClass} rounded-xl p-5 mb-6 text-white`}>
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium uppercase tracking-wide opacity-80">{roleName}</span>
              <h2 className="text-xl font-bold mt-1">{quizTitle}</h2>
              <p className="text-sm opacity-80 mt-1">{topicName}</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold">{currentQuestionIndex + 1}<span className="text-lg opacity-70">/{totalQuestions}</span></div>
              <div className="text-xs opacity-70">{completedCount} complete</div>
            </div>
          </div>

          {/* Progress Dots */}
          <div className="flex gap-1.5 mt-4 justify-center">
            {quizQuestions.map((q, idx) => {
              const qComplete = isQuestionAnswered(q);
              const isCurrent = idx === currentQuestionIndex;
              return (
                <button
                  key={q.id}
                  type="button"
                  onClick={() => handleJumpToQuestion(idx)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    isCurrent
                      ? 'bg-white scale-125'
                      : qComplete
                      ? 'bg-white/80'
                      : 'bg-white/30 hover:bg-white/50'
                  }`}
                  title={`Question ${idx + 1}${qComplete ? ' (complete)' : ''}`}
                />
              );
            })}
          </div>
        </div>

        {/* Instructions (show only on first question) */}
        {currentQuestionIndex === 0 && (
          <div className={`${accentBgClass} border-l-4 ${accentBorderClass} rounded-lg p-4 mb-6`}>
            <p className={`text-sm font-medium ${accentTextClass} mb-1`}>
              {quizInstructions}
            </p>
            <p className="text-xs text-gray-600">
              For each question, select your answer and rate your confidence.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Current Question */}
          {quizQuestions.length === 0 ? (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
              <p className="text-sm font-medium text-yellow-800">
                No quiz questions are available. Please contact the researcher.
              </p>
            </div>
          ) : (
            <div className="mb-6">
              {renderQuizQuestion(currentQuestion, currentQuestionIndex)}
            </div>
          )}

          {/* Error Message */}
          {quizSubmitted && !allComplete && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-sm font-medium text-red-700">
                Please complete all questions before submitting. {totalQuestions - completedCount} questions remaining.
              </p>
            </div>
          )}

          {/* Navigation Footer */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              {/* Previous Button */}
              <button
                type="button"
                onClick={handlePrev}
                disabled={!canGoPrev}
                className={`flex items-center gap-2 py-2 px-4 rounded-lg font-medium transition-all ${
                  canGoPrev
                    ? 'text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Previous
              </button>

              {/* Status */}
              <div className="text-center">
                {isCurrentComplete ? (
                  <span className="inline-flex items-center gap-1 text-sm text-green-600 font-medium">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Complete
                  </span>
                ) : (
                  <span className="text-sm text-gray-500">Answer all fields</span>
                )}
              </div>

              {/* Next/Submit Button */}
              {isLastQuestion ? (
                <button
                  type="submit"
                  className={`flex items-center gap-2 py-2 px-6 rounded-lg font-semibold text-white transition-all ${
                    allComplete ? buttonClass : 'bg-gray-400 cursor-not-allowed'
                  }`}
                >
                  Submit Quiz
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleNext}
                  className={`flex items-center gap-2 py-2 px-4 rounded-lg font-medium transition-all ${
                    isCurrentComplete
                      ? `${buttonClass} text-white`
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  Next
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
